import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ArrowRight, Check, Loader2, Sun, Moon } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useTheme } from "@/hooks/useTheme";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_aa20eee5-f3ca-4771-aefa-339a7569da1e/artifacts/qkx859ux_RESQ%20logo%20kolor%4010x.png";

const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;
const WEB3FORMS_ENDPOINT = "https://api.web3forms.com/submit";

const deviceTypes = [
  {
    id: "smith-nephew",
    name: "Smith & Nephew",
    description: "",
    image: "/images/devices/sn-device.png",
    contain: true,
  },
  {
    id: "nestle",
    name: "Nestle",
    description: "",
    image: "/images/devices/nestle-device.png",
    contain: true,
  },
  {
    id: "inne",
    name: "Inne",
    description: "Inne urządzenia medyczne",
    image: "/images/devices/inne.png",
    contain: false,
  },
];

export default function ServiceWizardPage() {
  const [step, setStep] = useState(1);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    deviceName: "",
    message: "",
    gdpr_consent: false,
  });

  const handleDeviceSelect = (device) => {
    setSelectedDevice(device);
    if (device.id === "nestle") {
      setFormData((prev) => ({ ...prev, deviceName: "Ella Compat" }));
    } else {
      setFormData((prev) => ({ ...prev, deviceName: "" }));
    }
  };

  const canProceedToStep2 = () => !!selectedDevice;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.gdpr_consent) {
      toast.error("Wymagana zgoda na przetwarzanie danych");
      return;
    }
    setIsSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("access_key", WEB3FORMS_KEY);
      fd.append("subject", `Nowe zgłoszenie serwisowe: ${selectedDevice.name}`);
      fd.append("from_name", formData.name || "Kreator serwisowy CKK ResQ");
      fd.append("name", formData.name);
      fd.append("email", formData.email);
      fd.append("phone", formData.phone || "(nie podano)");
      fd.append("producent", selectedDevice.name);
      fd.append("nazwa_urzadzenia", formData.deviceName);
      fd.append("opis_awarii", formData.message);
      fd.append("gdpr_consent", formData.gdpr_consent ? "TAK" : "NIE");
      fd.append("botcheck", "");

      // Try CORS first to get a real success/failure response.
      // Fall back to no-cors when CORS is blocked (e.g. by Cloudflare on some preview domains).
      let success = false;
      let errMsg = "";
      try {
        const response = await fetch(WEB3FORMS_ENDPOINT, {
          method: "POST",
          body: fd,
        });
        const data = await response.json();
        if (data?.success) {
          success = true;
        } else {
          errMsg = data?.message || "Web3Forms submission failed";
        }
      } catch (corsErr) {
        await fetch(WEB3FORMS_ENDPOINT, {
          method: "POST",
          mode: "no-cors",
          body: fd,
        });
        success = true; // optimistic: opaque response cannot be read
      }

      if (!success) {
        throw new Error(errMsg);
      }

      setIsSubmitted(true);
      toast.success("Zgłoszenie wysłane!");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Wystąpił błąd. Spróbuj ponownie.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetWizard = () => {
    setStep(1);
    setSelectedDevice(null);
    setIsSubmitted(false);
    setFormData({ name: "", email: "", phone: "", deviceName: "", message: "", gdpr_consent: false });
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0D0F12] relative">
      <div className="noise-overlay fixed inset-0 pointer-events-none" />
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-nav border-b border-gray-200 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <Link to="/" className="flex items-center" data-testid="wizard-logo">
              <img src={LOGO_URL} alt="ResQ Logo" className="h-10 lg:h-12 w-auto" />
            </Link>
            <div className="flex items-center gap-3">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full text-[#504D4D] dark:text-white/70 hover:text-[#EA8628] dark:hover:text-white transition-colors"
                data-testid="wizard-theme-toggle"
              >
                {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
              </button>
              <Link to="/" data-testid="wizard-back-home">
                <Button variant="ghost" className="text-[#504D4D] dark:text-white/70 hover:text-[#212738] dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Powrót do strony
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Progress indicator */}
          <div className="mb-12" data-testid="wizard-progress">
            <div className="flex items-center justify-center gap-4">
              <div className={`flex items-center gap-2 ${step >= 1 ? "text-[#EA8628]" : "text-gray-400 dark:text-white/40"}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold border-2 transition-colors ${
                  step >= 1 ? "border-[#EA8628] bg-[#EA8628]/20" : "border-gray-300 dark:border-white/20"
                }`}>
                  {step > 1 ? <Check className="w-5 h-5" /> : "1"}
                </div>
                <span className="hidden sm:inline font-medium">Wybierz Producenta</span>
              </div>
              <div className={`w-16 h-0.5 ${step >= 2 ? "bg-[#EA8628]" : "bg-gray-300 dark:bg-white/20"}`} />
              <div className={`flex items-center gap-2 ${step >= 2 ? "text-[#EA8628]" : "text-gray-400 dark:text-white/40"}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold border-2 transition-colors ${
                  step >= 2 ? "border-[#EA8628] bg-[#EA8628]/20" : "border-gray-300 dark:border-white/20"
                }`}>
                  {isSubmitted ? <Check className="w-5 h-5" /> : "2"}
                </div>
                <span className="hidden sm:inline font-medium">Zgłoszenie serwisowe</span>
              </div>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="step1" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.3 }}>
                <div className="text-center mb-10">
                  <h1 className="text-3xl sm:text-4xl font-bold text-[#212738] dark:text-white mb-4" data-testid="wizard-title">
                    Wybierz Producenta
                  </h1>
                  <p className="text-[#504D4D] dark:text-white/60 text-lg">Jakiego urządzenia dotyczy serwis?</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8" data-testid="device-selection-grid">
                  {deviceTypes.map((device) => (
                    <motion.button
                      key={device.id}
                      onClick={() => handleDeviceSelect(device)}
                      className={`relative overflow-hidden rounded-2xl border-2 transition-all duration-300 text-left group aspect-[4/3] ${
                        selectedDevice?.id === device.id
                          ? "border-[#EA8628]"
                          : "border-gray-200 dark:border-white/10 hover:border-gray-400 dark:hover:border-white/30"
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      data-testid={`device-tile-${device.id}`}
                    >
                      <div className="absolute inset-0 bg-[#202020]">
                        <img 
                          src={device.image} 
                          alt={device.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className={`absolute inset-0 bg-gradient-to-t from-[#202020]/95 via-[#202020]/30 to-transparent transition-opacity ${
                        selectedDevice?.id === device.id ? "opacity-100" : "opacity-80 group-hover:opacity-90"
                      }`} />
                      {selectedDevice?.id === device.id && <div className="absolute inset-0 bg-[#EA8628]/20" />}
                      {selectedDevice?.id === device.id && (
                        <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#EA8628] flex items-center justify-center z-10">
                          <Check className="w-5 h-5 text-white" />
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 p-5 z-10">
                        <h3 className={`text-xl font-bold mb-1 transition-colors ${
                          selectedDevice?.id === device.id ? "text-[#EA8628]" : "text-white"
                        }`}>{device.name}</h3>
                        {device.description && <p className="text-white/70 text-sm">{device.description}</p>}
                      </div>
                    </motion.button>
                  ))}
                </div>

                <div className="flex justify-center">
                  <Button
                    onClick={() => setStep(2)}
                    disabled={!canProceedToStep2()}
                    className="bg-[#EA8628] text-white font-semibold hover:bg-[#d4791f] disabled:opacity-50 disabled:cursor-not-allowed px-8 py-6 text-lg"
                    data-testid="wizard-next-button"
                  >
                    Dalej <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              </motion.div>
            )}

            {step === 2 && !isSubmitted && (
              <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
                <div className="text-center mb-10">
                  <h1 className="text-3xl sm:text-4xl font-bold text-[#212738] dark:text-white mb-4">Zgłoszenie serwisowe</h1>
                  <p className="text-[#504D4D] dark:text-white/60 text-base max-w-lg mx-auto mb-4">
                    Po otrzymaniu zgłoszenia serwisowego nasz serwisant skontaktuje się z Państwem w celu przygotowania wyceny czynności serwisowych.
                  </p>
                  <p className="text-[#504D4D] dark:text-white/60 text-lg">
                    Producent: <span className="text-[#EA8628] font-semibold">{selectedDevice?.name}</span>
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="max-w-xl mx-auto space-y-6" data-testid="wizard-contact-form">
                  <div>
                    <Label htmlFor="deviceName" className="text-[#212738] dark:text-white/80 mb-2 block">Nazwa urządzenia *</Label>
                    <Input id="deviceName" name="deviceName" value={formData.deviceName} onChange={handleInputChange} required
                      placeholder="np. Aparat EKG, Monitor pacjenta..."
                      className="bg-[#F7F9F9] dark:bg-white/5 border-gray-200 dark:border-white/20 text-[#212738] dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40 focus:border-[#EA8628]"
                      data-testid="wizard-input-device-name" />
                  </div>
                  <div>
                    <Label htmlFor="name" className="text-[#212738] dark:text-white/80 mb-2 block">Imię i nazwisko *</Label>
                    <Input id="name" name="name" value={formData.name} onChange={handleInputChange} required placeholder="Jan Kowalski"
                      className="bg-[#F7F9F9] dark:bg-white/5 border-gray-200 dark:border-white/20 text-[#212738] dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40 focus:border-[#EA8628]"
                      data-testid="wizard-input-name" />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-[#212738] dark:text-white/80 mb-2 block">Adres e-mail *</Label>
                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} required placeholder="jan@firma.pl"
                      className="bg-[#F7F9F9] dark:bg-white/5 border-gray-200 dark:border-white/20 text-[#212738] dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40 focus:border-[#EA8628]"
                      data-testid="wizard-input-email" />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-[#212738] dark:text-white/80 mb-2 block">Numer telefonu (opcjonalnie)</Label>
                    <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} placeholder="+48 123 456 789"
                      className="bg-[#F7F9F9] dark:bg-white/5 border-gray-200 dark:border-white/20 text-[#212738] dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40 focus:border-[#EA8628]"
                      data-testid="wizard-input-phone" />
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-[#212738] dark:text-white/80 mb-2 block">Opis awarii *</Label>
                    <Textarea id="message" name="message" value={formData.message} onChange={handleInputChange} required
                      placeholder="Opisz problem z urządzeniem..." rows={4}
                      className="bg-[#F7F9F9] dark:bg-white/5 border-gray-200 dark:border-white/20 text-[#212738] dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/40 focus:border-[#EA8628] resize-none"
                      data-testid="wizard-input-message" />
                  </div>
                  <div className="flex items-start gap-3">
                    <Checkbox id="gdpr_consent" checked={formData.gdpr_consent}
                      onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, gdpr_consent: checked }))}
                      className="border-gray-300 dark:border-white/30 data-[state=checked]:bg-[#EA8628] data-[state=checked]:border-[#EA8628] mt-1"
                      data-testid="wizard-gdpr-checkbox" />
                    <Label htmlFor="gdpr_consent" className="text-[#504D4D] dark:text-white/60 text-sm leading-relaxed cursor-pointer">
                      Wyrażam zgodę na przetwarzanie moich danych osobowych w celu odpowiedzi na zapytanie. *
                    </Label>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    <Button type="button" variant="outline" onClick={() => setStep(1)}
                      className="border-gray-300 dark:border-white/20 text-[#504D4D] dark:text-white hover:bg-gray-100 dark:hover:bg-white/10 px-6 py-5"
                      data-testid="wizard-back-button">
                      <ArrowLeft className="mr-2 h-4 w-4" /> Wróć
                    </Button>
                    <Button type="submit" disabled={isSubmitting}
                      className="flex-1 bg-[#EA8628] text-white font-semibold hover:bg-[#d4791f] disabled:opacity-50 px-8 py-5 text-lg"
                      data-testid="wizard-submit-button">
                      {isSubmitting ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" />Wysyłanie...</>) : "Wyślij zapytanie"}
                    </Button>
                  </div>
                </form>
              </motion.div>
            )}

            {isSubmitted && (
              <motion.div key="success" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }} className="text-center py-12">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  className="w-24 h-24 mx-auto mb-8 rounded-full bg-[#EA8628]/20 flex items-center justify-center">
                  <Check className="w-12 h-12 text-[#EA8628]" />
                </motion.div>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#212738] dark:text-white mb-4" data-testid="success-title">Zgłoszenie wysłane!</h2>
                <p className="text-[#504D4D] dark:text-white/60 text-lg mb-8 max-w-md mx-auto">
                  Dziękujemy za zapytanie. Nasz zespół skontaktuje się z Tobą w ciągu 24 godzin.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button onClick={resetWizard} variant="outline"
                    className="border-gray-300 dark:border-white/20 text-[#504D4D] dark:text-white hover:bg-gray-100 dark:hover:bg-white/10 px-6 py-5"
                    data-testid="wizard-new-request-button">Nowe zgłoszenie</Button>
                  <Link to="/">
                    <Button className="bg-[#EA8628] text-white font-semibold hover:bg-[#d4791f] px-6 py-5" data-testid="wizard-home-button">
                      Powrót do strony głównej
                    </Button>
                  </Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
